import Cookies from "js-cookie";
import httpClient from "../../../../api/httpClient";
import { getDefaultAssignedOfficeCode } from "../../../../utils/assignedOffices";

const BASE = "/api/carrier-sourcing";

export function getCarrierSourcingOfficeCode() {
  return (
    Cookies.get("officeCode") ||
    getDefaultAssignedOfficeCode() ||
    ""
  ).toString().trim();
}

function officeHeaders(officeCode) {
  const code = (officeCode || getCarrierSourcingOfficeCode()).toString().trim();
  return code ? { "Office-Code": code } : {};
}

function cfg(officeCode, extra = {}) {
  return {
    ...extra,
    headers: {
      ...officeHeaders(officeCode),
      ...(extra.headers || {}),
    },
  };
}

export const carrierSourcingApi = {
  getPipeline: ({ officeCode, stage, riskTier, search, page = 0, size = 50, sort } = {}) =>
    httpClient.get(`${BASE}/pipeline`, cfg(officeCode, {
      params: {
        stage: stage || undefined,
        riskTier: riskTier || undefined,
        search: search || undefined,
        page,
        size,
        sort,
      },
    })),

  createCarrier: (body, officeCode) =>
    httpClient.post(`${BASE}/carriers`, body, cfg(officeCode)),

  getCarrier: (carrierId, officeCode) =>
    httpClient.get(`${BASE}/carriers/${carrierId}`, cfg(officeCode)),

  updateCarrier: (carrierId, body, officeCode) =>
    httpClient.put(`${BASE}/carriers/${carrierId}`, body, cfg(officeCode)),

  updateStage: (carrierId, body, officeCode) =>
    httpClient.patch(`${BASE}/carriers/${carrierId}/stage`, body, cfg(officeCode)),

  createRateRequest: (carrierId, body, officeCode) =>
    httpClient.post(`${BASE}/carriers/${carrierId}/rate-requests`, body, cfg(officeCode)),

  listRateDocuments: (carrierId, officeCode) =>
    httpClient.get(`${BASE}/carriers/${carrierId}/rate-documents`, cfg(officeCode)),

  uploadRateDocument: (carrierId, file, officeCode) => {
    const form = new FormData();
    form.append("file", file);
    // Do not set Content-Type manually — axios must add the multipart boundary.
    return httpClient.post(
      `${BASE}/carriers/${carrierId}/rate-documents`,
      form,
      cfg(officeCode)
    );
  },

  extractRateDocument: (carrierId, documentId, officeCode) =>
    httpClient.post(
      `${BASE}/carriers/${carrierId}/rate-documents/${documentId}/extract`,
      {},
      cfg(officeCode)
    ),

  updateRateDocumentStatus: (carrierId, documentId, status, officeCode) =>
    httpClient.patch(
      `${BASE}/carriers/${carrierId}/rate-documents/${documentId}/status`,
      { status },
      cfg(officeCode)
    ),

  listRateVersions: (carrierId, officeCode) =>
    httpClient.get(`${BASE}/carriers/${carrierId}/rate-versions`, cfg(officeCode)),

  listLaneRates: (carrierId, versionId, officeCode) =>
    httpClient.get(
      `${BASE}/carriers/${carrierId}/rate-versions/${versionId}/lane-rates`,
      cfg(officeCode)
    ),

  getCompliance: (carrierId, officeCode) =>
    httpClient.get(`${BASE}/carriers/${carrierId}/compliance`, cfg(officeCode)),

  refreshCompliance: (carrierId, officeCode) =>
    httpClient.post(
      `${BASE}/carriers/${carrierId}/compliance/refresh`,
      null,
      cfg(officeCode)
    ),

  verifyComplianceCheck: (carrierId, checkId, body, officeCode) =>
    httpClient.post(
      `${BASE}/carriers/${carrierId}/compliance/checks/${checkId}/verify`,
      body,
      cfg(officeCode)
    ),

  /** Upload COI/insurance PDF — backend OCRs MC/insured name + policy dates. */
  uploadInsuranceDocument: (carrierId, file, officeCode) => {
    const form = new FormData();
    form.append("file", file);
    // OCR can take a while; do not set Content-Type (axios adds multipart boundary).
    return httpClient.post(
      `${BASE}/carriers/${carrierId}/compliance/insurance-document`,
      form,
      cfg(officeCode, { timeout: 120000 })
    );
  },

  getCarrierScore: (carrierId, officeCode) =>
    httpClient.get(`${BASE}/carriers/${carrierId}/score`, cfg(officeCode)),

  listThreads: (carrierId, { search, officeCode } = {}) =>
    httpClient.get(`${BASE}/carriers/${carrierId}/threads`, cfg(officeCode, {
      params: { search: search || undefined },
    })),

  createThread: (carrierId, body, officeCode) =>
    httpClient.post(`${BASE}/carriers/${carrierId}/threads`, body, cfg(officeCode)),

  listThreadEntries: (carrierId, threadId, officeCode) =>
    httpClient.get(
      `${BASE}/carriers/${carrierId}/threads/${threadId}/entries`,
      cfg(officeCode)
    ),

  addThreadEntry: (carrierId, threadId, body, officeCode) =>
    httpClient.post(
      `${BASE}/carriers/${carrierId}/threads/${threadId}/entries`,
      body,
      cfg(officeCode)
    ),

  listDocuments: (carrierId, officeCode) =>
    httpClient.get(`${BASE}/carriers/${carrierId}/documents`, cfg(officeCode)),

  uploadDocument: (carrierId, file, metadata = {}, officeCode) => {
    const form = new FormData();
    form.append("file", file);
    const meta = {
      documentType: metadata.documentType || "OTHER",
      documentVersion:
        metadata.documentVersion != null ? Number(metadata.documentVersion) : 1,
    };
    if (metadata.expiryDate) {
      meta.expiryDate = metadata.expiryDate;
    }
    // Filename helps Spring bind @RequestPart("metadata") as JSON.
    form.append(
      "metadata",
      new Blob([JSON.stringify(meta)], { type: "application/json" }),
      "metadata.json"
    );
    // Do not set Content-Type manually — axios must add the multipart boundary.
    return httpClient.post(
      `${BASE}/carriers/${carrierId}/documents`,
      form,
      cfg(officeCode)
    );
  },

  getCarrierOnboarding: (carrierId, officeCode) =>
    httpClient.get(`${BASE}/carriers/${carrierId}/onboarding`, cfg(officeCode)),

  getFunnelReport: (officeCode) =>
    httpClient.get(`${BASE}/reports/funnel`, cfg(officeCode)),

  getLaneCoverage: (officeCode) =>
    httpClient.get(`${BASE}/reports/lane-coverage`, cfg(officeCode)),

  getRenewals: (officeCode, daysAhead = 90) =>
    httpClient.get(`${BASE}/reports/renewals`, cfg(officeCode, {
      params: { daysAhead },
    })),
};

export default carrierSourcingApi;
